import { format, parseISO } from "date-fns";
import { formatMoney } from "@/features/clients/constants";
import type { PurchaseOrder } from "@/features/purchase/types";
import type { Company } from "@/features/settings/types";

/** Formal print/PDF surface — only this block should appear when printing the PO detail page. */
export function PurchaseOrderPrintDocument({
  po,
  company,
}: {
  po: PurchaseOrder;
  company: Company | null;
}) {
  const issue = safeDate(po.issue_date);
  const due = po.due_date ? safeDate(po.due_date) : null;
  const lines = po.lines ?? [];

  return (
    <div className="po-print hidden print:block text-black">
      <header className="mb-6 border-b border-black pb-4">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-xl font-bold tracking-tight">{company?.name || "Purchase Order"}</h1>
            {company ? (
              <div className="mt-1 space-y-0.5 text-[11px] leading-snug text-neutral-700">
                {[company.addr1, company.addr2, [company.city, company.state, company.pincode].filter(Boolean).join(", ")]
                  .filter(Boolean)
                  .map((line) => (
                    <div key={String(line)}>{line}</div>
                  ))}
                {company.gstin ? <div>GSTIN: {company.gstin}</div> : null}
                {company.phone || company.email ? (
                  <div>
                    {[company.phone, company.email].filter(Boolean).join(" · ")}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="text-right">
            <div className="text-lg font-bold">PURCHASE ORDER</div>
            <div className="mt-1 font-mono text-base font-semibold">{po.po_number}</div>
            <div className="mt-2 text-[11px] text-neutral-700">
              <div>Date: {issue}</div>
              {due ? <div>Due: {due}</div> : null}
              <div className="capitalize">Status: {po.status}</div>
            </div>
          </div>
        </div>
      </header>

      <section className="mb-5 grid grid-cols-2 gap-4 text-[11px]">
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
            Vendor
          </div>
          <div className="font-semibold">
            {po.vendor_code ? `${po.vendor_code} · ` : ""}
            {po.vendor_name || "—"}
          </div>
          {po.payment_terms ? (
            <div className="mt-1 text-neutral-700">Payment: {po.payment_terms}</div>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
              Bill to
            </div>
            <pre className="whitespace-pre-wrap font-sans text-neutral-800">
              {po.bill_to_text || "—"}
            </pre>
          </div>
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
              Ship to
            </div>
            <pre className="whitespace-pre-wrap font-sans text-neutral-800">
              {po.ship_to_text || "—"}
            </pre>
          </div>
        </div>
      </section>

      <table className="mb-4 w-full border-collapse text-[11px]">
        <thead>
          <tr className="border-y border-black bg-neutral-100">
            <th className="px-1.5 py-1.5 text-left font-semibold">#</th>
            <th className="px-1.5 py-1.5 text-left font-semibold">Material</th>
            <th className="px-1.5 py-1.5 text-right font-semibold">Qty</th>
            <th className="px-1.5 py-1.5 text-right font-semibold">Weight</th>
            <th className="px-1.5 py-1.5 text-left font-semibold">UOM</th>
            <th className="px-1.5 py-1.5 text-right font-semibold">Rate</th>
            <th className="px-1.5 py-1.5 text-right font-semibold">GST</th>
            <th className="px-1.5 py-1.5 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-neutral-300 align-top">
              <td className="px-1.5 py-1.5 tabular-nums">{line.line_no}</td>
              <td className="px-1.5 py-1.5">
                <div className="font-medium">{line.material_name || "—"}</div>
                <div className="text-[10px] text-neutral-600">
                  {[
                    line.description,
                    line.grade_name,
                    line.shape_label,
                    line.thickness != null ? `Thk ${line.thickness}` : null,
                    line.width != null ? `W ${line.width}` : null,
                    line.od != null ? `Ø${line.od}` : null,
                    line.length != null ? `L ${line.length}` : null,
                  ]
                    .filter(Boolean)
                    .filter((v, i, arr) => arr.indexOf(v) === i)
                    .slice(0, 4)
                    .join(" · ") || null}
                </div>
              </td>
              <td className="px-1.5 py-1.5 text-right tabular-nums">{line.qty || "—"}</td>
              <td className="px-1.5 py-1.5 text-right tabular-nums">{line.weight || "—"}</td>
              <td className="px-1.5 py-1.5">{line.uom_code || "—"}</td>
              <td className="px-1.5 py-1.5 text-right tabular-nums">{formatMoney(line.unit_rate)}</td>
              <td className="px-1.5 py-1.5 text-right tabular-nums">{line.gst_slab}%</td>
              <td className="px-1.5 py-1.5 text-right tabular-nums font-medium">
                {formatMoney(line.line_subtotal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mb-5 flex justify-end">
        <div className="w-56 space-y-1 text-[11px]">
          <Tot row="Subtotal" value={formatMoney(po.subtotal)} />
          {po.discount ? <Tot row="Discount" value={formatMoney(po.discount)} /> : null}
          <Tot row="Tax" value={formatMoney(po.tax_total)} />
          {po.adjustment ? <Tot row="Adjustment" value={formatMoney(po.adjustment)} /> : null}
          <div className="flex justify-between border-t border-black pt-1.5 text-sm font-bold">
            <span>Grand total</span>
            <span className="tabular-nums">{formatMoney(po.grand_total)}</span>
          </div>
        </div>
      </div>

      {(po.notes || po.terms) && (
        <section className="mb-6 grid grid-cols-2 gap-4 text-[11px]">
          {po.notes ? (
            <div>
              <div className="mb-1 font-semibold">Notes</div>
              <p className="whitespace-pre-wrap text-neutral-700">{po.notes}</p>
            </div>
          ) : null}
          {po.terms ? (
            <div>
              <div className="mb-1 font-semibold">Terms</div>
              <p className="whitespace-pre-wrap text-neutral-700">{po.terms}</p>
            </div>
          ) : null}
        </section>
      )}

      <footer className="mt-10 grid grid-cols-2 gap-8 text-[11px]">
        <div className="border-t border-neutral-400 pt-2">Prepared by</div>
        <div className="border-t border-neutral-400 pt-2 text-right">Authorized signatory</div>
      </footer>
    </div>
  );
}

function Tot({ row, value }: { row: string; value: string }) {
  return (
    <div className="flex justify-between gap-6">
      <span className="text-neutral-600">{row}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function safeDate(iso: string) {
  try {
    return format(parseISO(iso), "dd MMM yyyy");
  } catch {
    return iso;
  }
}
